import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Upload, Loader2, Trash2, FileText, Sparkles, CheckCircle2, Send, CalendarClock, ThumbsUp, ThumbsDown, Edit3, Clock } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency, formatDateShort, formatInvoiceStatus, getInvoiceStatusColor, formatPaymentStatus, getPaymentStatusColor } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Convert axios error -> readable string. FastAPI 422 returns `detail` as a
// list of validation errors which can break sonner's toast (white page) when
// passed raw, so flatten to text here.
const formatAxiosError = (e, fallback = 'Erreur') => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join('; ');
  }
  return fallback;
};

const PAYMENT_MODES = [
  { value: 'virement', label: 'Virement' },
  { value: 'prelevement', label: 'Prélèvement SEPA' },
  { value: 'carte', label: 'Carte bancaire' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'especes', label: 'Espèces' },
];

// Taux de TVA en vigueur en France
const FRENCH_TVA_RATES = [
  { value: '20', label: '20 % — Taux normal' },
  { value: '10', label: '10 % — Taux intermédiaire (restauration, transport)' },
  { value: '5.5', label: '5,5 % — Taux réduit (alimentaire, livres, énergie)' },
  { value: '2.1', label: '2,1 % — Super réduit (médicaments remboursés, presse)' },
  { value: '0', label: '0 % — Exonération / Hors champ' },
];

/**
 * State-driven action panel shown inside the invoice dialog.
 * Choses les boutons selon workflow_status + permissions de l'utilisateur.
 */
function ActionPanel({ invoice, hasPermission, onSubmit, onApprove, onReject, onRequestModification, onMarkPaid, onSchedule, onSepa, onUnmarkPaid }) {
  if (!invoice) return null;
  const ws = invoice.workflow_status;
  const ps = invoice.payment_status;
  const isPaid = ps === 'payee';
  const canPay = hasPermission('invoices', 'pay');

  // Already paid → summary + ability to revert paid status if user made a mistake
  if (isPaid) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <p className="font-semibold text-emerald-900">Facture payée</p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs text-emerald-700">
          <div><span className="text-emerald-500">Date</span><br/><strong>{invoice.date_paiement || '-'}</strong></div>
          <div><span className="text-emerald-500">Mode</span><br/><strong>{invoice.mode_paiement || '-'}</strong></div>
          <div><span className="text-emerald-500">Référence</span><br/><strong>{invoice.reference_paiement || '-'}</strong></div>
        </div>
        {canPay && onUnmarkPaid && (
          <Button onClick={onUnmarkPaid} size="sm" variant="outline" className="mt-3 w-full text-emerald-800 border-emerald-300">
            <Edit3 className="w-3.5 h-3.5 mr-2" /> Annuler le paiement (rétablir non payée)
          </Button>
        )}
      </div>
    );
  }

  // brouillon / modification_demandee → submit to validation
  if (ws === 'brouillon' || ws === 'modification_demandee') {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Edit3 className="w-4 h-4 text-blue-700" />
          <p className="font-semibold text-blue-900">{ws === 'modification_demandee' ? 'Modification demandée' : 'Brouillon'}</p>
        </div>
        <p className="text-xs text-blue-700 mb-3">Une fois la saisie complète, soumettez la facture au workflow de validation.</p>
        <Button onClick={onSubmit} className="w-full bg-[#0F5156] hover:bg-[#0d464a]">
          <Send className="w-4 h-4 mr-2" /> Soumettre à validation
        </Button>
      </div>
    );
  }

  // en_validation → approve/reject/modify (server-side check via can_user_validate)
  if (ws === 'en_validation') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-amber-700" />
          <p className="font-semibold text-amber-900">En validation — étape {invoice.current_step_index + 1}{invoice.current_step_name ? `: ${invoice.current_step_name}` : ''}</p>
        </div>
        <p className="text-xs text-amber-700 mb-3">Si vous êtes le validateur de cette étape, prenez une décision ci-dessous.</p>
        <div className="grid grid-cols-3 gap-2">
          <Button onClick={onApprove} size="sm" className="bg-green-600 hover:bg-green-700">
            <ThumbsUp className="w-3.5 h-3.5 mr-1" /> Approuver
          </Button>
          <Button onClick={onRequestModification} size="sm" variant="outline" className="text-orange-700 border-orange-300">
            <Edit3 className="w-3.5 h-3.5 mr-1" /> Modifier
          </Button>
          <Button onClick={onReject} size="sm" variant="outline" className="text-red-700 border-red-300">
            <ThumbsDown className="w-3.5 h-3.5 mr-1" /> Rejeter
          </Button>
        </div>
      </div>
    );
  }

  // validee + non payée → pay actions
  if (ws === 'validee') {
    const canSepa = hasPermission('tresorerie', 'sepa');
    if (!canPay && !canSepa) {
      return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-slate-600">Facture validée. Permission requise pour le paiement.</p>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 p-4">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <p className="font-semibold text-emerald-900">Validée — choisir le paiement</p>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {canPay && (
            <Button onClick={onMarkPaid} variant="outline" className="justify-start text-emerald-800 border-emerald-300">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Marquer comme payée
            </Button>
          )}
          {canPay && (
            <Button onClick={onSchedule} variant="outline" className="justify-start text-blue-800 border-blue-300">
              <CalendarClock className="w-4 h-4 mr-2" /> Programmer le paiement
            </Button>
          )}
          {canSepa && (
            <Button onClick={onSepa} variant="outline" className="justify-start text-indigo-800 border-indigo-300">
              <Send className="w-4 h-4 mr-2" /> Générer fichier SEPA (XML)
            </Button>
          )}
        </div>
      </div>
    );
  }

  // rejetee → read-only
  if (ws === 'rejetee') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-center gap-2 mb-1">
          <ThumbsDown className="w-4 h-4 text-red-700" />
          <p className="font-semibold text-red-900">Facture rejetée</p>
        </div>
        <p className="text-xs text-red-700">Cette facture a été rejetée et ne peut plus être traitée.</p>
      </div>
    );
  }

  return null;
}

const PAYMENT_STATUSES = [
  { value: 'non_payee', label: 'Non payée' },
  { value: 'payee', label: 'Payée' },
];

const emptyForm = {
  supplier_id: '',
  invoice_number: '',
  date_facture: '',
  date_echeance: '',
  description: '',
  mode_paiement: '',
  payment_status: 'non_payee',
  date_paiement: '',
  reference_paiement: '',
  montant_ht: 0,
  montant_ttc: 0,
  tva_breakdown: [{ taux: '20', base_ht: 0, montant_tva: 0, compte_comptable: '' }],
  type_document: 'facture',
};

export default function Invoices() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [comptesCharges, setComptesCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfFileName, setPdfFileName] = useState('');
  const [insightSupplier, setInsightSupplier] = useState(null); // { nom, siret, ... } if supplier missing
  const [editingInvoiceId, setEditingInvoiceId] = useState(null); // brouillon created by AI or re-opened
  const [editingInvoice, setEditingInvoice] = useState(null); // full invoice data when re-opening (status, payment_status, etc.)
  const [usage, setUsage] = useState(null);
  // Payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null); // invoice
  const [payForm, setPayForm] = useState({ date_paiement: '', mode_paiement: 'virement', reference_paiement: '' });
  const [payLoading, setPayLoading] = useState(false);
  // Supplier creation dialog
  const [supDialogOpen, setSupDialogOpen] = useState(false);
  const [supSearch, setSupSearch] = useState('');
  const [supResults, setSupResults] = useState([]);
  const [supSearching, setSupSearching] = useState(false);
  const [supForm, setSupForm] = useState({ nom: '', siret: '', adresse: '', code_postal: '', ville: '', email: '', telephone: '', iban: '', bic: '', tva_intracommunautaire: '' });
  const [supSaving, setSupSaving] = useState(false);
  // Validation actions (approve / reject / modify)
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationAction, setValidationAction] = useState(null); // 'approve' | 'reject' | 'request-modification'
  const [validationComment, setValidationComment] = useState('');
  const [validationLoading, setValidationLoading] = useState(false);
  // Schedule payment
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ scheduled_date: '', mode_paiement: 'prelevement', bank_account_id: '', notes: '' });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  // SEPA generation
  const [sepaOpen, setSepaOpen] = useState(false);
  const [sepaForm, setSepaForm] = useState({ date_execution: '', bank_account_id: '' });
  const [sepaLoading, setSepaLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  // Post-SEPA payment confirmation (single invoice flow)
  const [postSepaOpen, setPostSepaOpen] = useState(false);
  const [postSepa, setPostSepa] = useState(null); // { invoice_id, reference, date_execution }
  const [postSepaLoading, setPostSepaLoading] = useState(false);
  // Batch SEPA: multi-select on the Validées tab
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sepaBatchMode, setSepaBatchMode] = useState(false);
  const [batchPostSepaOpen, setBatchPostSepaOpen] = useState(false);
  const [batchPostSepa, setBatchPostSepa] = useState(null); // { invoice_ids, reference, date_execution }
  const [batchPostSepaLoading, setBatchPostSepaLoading] = useState(false);

  const fetchUsage = async () => {
    try { const res = await axios.get(`${API_URL}/supplier-invoices/usage`); setUsage(res.data); } catch (e) {}
  };

  const fetchInvoices = async () => {
    try {
      const params = { skip: 0, limit: 500, type_document: 'facture' };
      if (tab !== 'all') params.workflow_status = tab;
      if (search) params.q = search;
      if (supplierFilter && supplierFilter !== 'all') params.supplier_id = supplierFilter;
      const res = await axios.get(`${API_URL}/supplier-invoices`, { params });
      setInvoices(res.data);
    } catch (e) { toast.error('Erreur chargement factures'); }
    finally { setLoading(false); }
  };

  const fetchSuppliers = async () => {
    try { const res = await axios.get(`${API_URL}/suppliers`, { params: { limit: 500 } }); setSuppliers(res.data); } catch (e) {}
  };

  const fetchComptes = async () => {
    try {
      const res = await axios.get(`${API_URL}/comptabilite/comptes`, { params: { classe: '6' } });
      setComptesCharges(res.data || []);
    } catch (e) {}
  };

  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/bank-accounts`);
      setBankAccounts(res.data || []);
    } catch (e) {}
  };

  useEffect(() => { fetchSuppliers(); fetchUsage(); fetchComptes(); fetchBankAccounts(); }, []);
  useEffect(() => { setLoading(true); fetchInvoices(); }, [tab, search, supplierFilter]);

  const totalHt = useMemo(() => form.tva_breakdown.reduce((s, r) => s + (parseFloat(r.base_ht) || 0), 0), [form.tva_breakdown]);
  const totalTva = useMemo(() => form.tva_breakdown.reduce((s, r) => s + (parseFloat(r.montant_tva) || 0), 0), [form.tva_breakdown]);
  const totalTtc = totalHt + totalTva;

  const addTvaRow = () => setForm(f => ({ ...f, tva_breakdown: [...f.tva_breakdown, { taux: '20', base_ht: 0, montant_tva: 0, compte_comptable: '' }] }));
  const removeTvaRow = (i) => setForm(f => ({ ...f, tva_breakdown: f.tva_breakdown.filter((_, idx) => idx !== i) }));
  const updateTvaRow = (i, field, value) => setForm(f => {
    const next = [...f.tva_breakdown];
    next[i] = { ...next[i], [field]: value };
    // Auto-calc montant_tva when base or taux changes
    if (field === 'base_ht' || field === 'taux') {
      const base = parseFloat(field === 'base_ht' ? value : next[i].base_ht) || 0;
      const tx = parseFloat(field === 'taux' ? value : next[i].taux) || 0;
      next[i].montant_tva = Math.round(base * tx) / 100;
    }
    return { ...f, tva_breakdown: next };
  });

  const handleSave = async () => {
    if (!form.supplier_id || !form.invoice_number || !form.date_facture || !form.date_echeance) {
      toast.error('Champs obligatoires manquants'); return;
    }
    setSaving(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        invoice_number: form.invoice_number,
        date_facture: form.date_facture,
        date_echeance: form.date_echeance,
        description: form.description || null,
        mode_paiement: form.mode_paiement || null,
        type_document: form.type_document || 'facture',
        tva_breakdown: form.tva_breakdown.map(r => ({
          taux: parseFloat(r.taux) || 0,
          base_ht: parseFloat(r.base_ht) || 0,
          montant_tva: parseFloat(r.montant_tva) || 0,
          compte_comptable: r.compte_comptable || null,
        })),
        montant_ht: Math.round(totalHt * 100) / 100,
        montant_tva: Math.round(totalTva * 100) / 100,
        montant_ttc: Math.round(totalTtc * 100) / 100,
        taux_tva_principal: form.tva_breakdown[0]?.taux || 20,
        lignes: [],
      };

      let workflow_status;
      let invoice_id;
      if (editingInvoiceId) {
        await axios.put(`${API_URL}/supplier-invoices/${editingInvoiceId}`, payload);
        const res = await axios.post(`${API_URL}/supplier-invoices/${editingInvoiceId}/submit`);
        workflow_status = res.data?.workflow_status;
        invoice_id = editingInvoiceId;
      } else {
        const res = await axios.post(`${API_URL}/supplier-invoices`, payload);
        workflow_status = res.data?.workflow_status;
        invoice_id = res.data?.id;
      }

      // If user marked as paid and workflow already validee (auto-approved
      // because they are the validator on the default 1-step workflow),
      // record the payment now. Otherwise inform that payment is pending.
      let paidAlso = false;
      if (form.payment_status === 'payee' && invoice_id) {
        if (workflow_status === 'validee') {
          try {
            await axios.put(`${API_URL}/supplier-invoices/${invoice_id}/payment`, {
              payment_status: 'payee',
              date_paiement: form.date_paiement || new Date().toISOString().slice(0, 10),
              mode_paiement: form.mode_paiement || null,
              reference_paiement: form.reference_paiement || null,
            });
            paidAlso = true;
          } catch (e) {
            toast.error('Facture validée mais paiement non appliqué : ' + (e.response?.data?.detail || ''));
          }
        } else {
          toast.info('Paiement en attente : la facture doit être validée par le workflow avant marquage payée.');
        }
      }

      const statusLabel = workflow_status === 'en_validation' ? 'soumise au workflow' : workflow_status === 'validee' ? 'validée' : 'enregistrée';
      const payLabel = paidAlso ? ' + payée' : '';
      toast.success(`Facture ${statusLabel}${payLabel}`);
      setCreateOpen(false);
      setForm(emptyForm);
      setPdfPreviewUrl('');
      setPdfFileName('');
      setInsightSupplier(null);
      setEditingInvoiceId(null);
      fetchInvoices();
      fetchUsage();
    } catch (e) {
      toast.error(formatAxiosError(e, 'Erreur enregistrement'));
    } finally { setSaving(false); }
  };

  const handleCreateSupplierFromInsight = () => {
    if (!insightSupplier) return;
    openSupplierDialog(insightSupplier);
  };

  // Inline confirm: create the proposed supplier and link it to the current draft
  // in one call (uses suppliers.create_from_invoice permission).
  const handleConfirmProposedSupplier = async () => {
    if (!insightSupplier || !editingInvoiceId) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/supplier-invoices/${editingInvoiceId}/confirm-proposed-supplier`,
        {
          nom: insightSupplier.nom,
          siret: insightSupplier.siret || null,
          adresse: insightSupplier.adresse || null,
          code_postal: insightSupplier.code_postal || null,
          ville: insightSupplier.ville || null,
          pays: insightSupplier.pays || 'France',
          telephone: insightSupplier.telephone || null,
          email: insightSupplier.email || null,
          iban: insightSupplier.iban || null,
          bic: insightSupplier.bic || null,
          tva_intracommunautaire: insightSupplier.tva_intracommunautaire || null,
        }
      );
      const newSupplier = res.data?.supplier;
      await fetchSuppliers();
      if (newSupplier?.id) {
        setForm(f => ({ ...f, supplier_id: newSupplier.id }));
      }
      setInsightSupplier(null);
      toast.success(`Fournisseur "${newSupplier?.nom}" créé et lié à la facture`);
    } catch (e) {
      toast.error(formatAxiosError(e, 'Erreur création fournisseur'));
    } finally { setSaving(false); }
  };

  const readFileAsDataUrl = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

  const handleAiImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiLoading(true);
    try {
      // Generate preview URL for the right-pane PDF/image preview
      const dataUrl = await readFileAsDataUrl(file);
      setPdfPreviewUrl(dataUrl);
      setPdfFileName(file.name);

      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/ai/read-invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = res.data;

      const facture = data.facture || {};
      const tot = data.totaux || {};
      const breakdown = Array.isArray(data.tva_breakdown) && data.tva_breakdown.length
        ? data.tva_breakdown.map(r => ({
            taux: String(parseFloat(r.taux) || 20),
            base_ht: parseFloat(r.base_ht) || 0,
            montant_tva: parseFloat(r.montant_tva) || 0,
            compte_comptable: r.compte_charge || r.compte_comptable || '',
          }))
        : [{
            taux: '20',
            base_ht: parseFloat(tot.montant_ht) || 0,
            montant_tva: parseFloat(tot.montant_tva) || 0,
            compte_comptable: '',
          }];

      // Resolve supplier_id from existing match (auto-creation removed — now proposed)
      let supplier_id = '';
      let supplier_default_compte = '';
      if (data.existing_supplier?.id) {
        supplier_id = data.existing_supplier.id;
        supplier_default_compte = data.existing_supplier.compte_comptable_default || '';
      } else if (data.fournisseur?.nom) {
        const match = suppliers.find(s => s.nom.toLowerCase().includes(data.fournisseur.nom.toLowerCase()));
        if (match) {
          supplier_id = match.id;
          supplier_default_compte = match.compte_comptable_default || '';
        }
      }

      // No supplier match -> show the proposed supplier panel for explicit confirmation
      if (!supplier_id && data.proposed_supplier) {
        setInsightSupplier(data.proposed_supplier);
      } else {
        setInsightSupplier(null);
      }

      // Fallback: rows without compte_comptable inherit supplier default
      if (supplier_default_compte) {
        breakdown.forEach(r => { if (!r.compte_comptable) r.compte_comptable = supplier_default_compte; });
      }

      setForm({
        ...emptyForm,
        supplier_id,
        invoice_number: facture.numero || '',
        date_facture: facture.date_facture || '',
        date_echeance: facture.date_echeance || '',
        mode_paiement: facture.mode_paiement || '',
        montant_ht: parseFloat(tot.montant_ht) || 0,
        montant_ttc: parseFloat(tot.montant_ttc) || 0,
        tva_breakdown: breakdown,
      });
      setEditingInvoiceId(data.invoice_id || null);
      // Fetch the freshly-created brouillon so the ActionPanel renders
      // ("Soumettre a validation" + "Supprimer" en mode brouillon)
      if (data.invoice_id) {
        try {
          const inv = await axios.get(`${API_URL}/supplier-invoices/${data.invoice_id}`);
          setEditingInvoice(inv.data);
        } catch (e) {}
      } else {
        setEditingInvoice(null);
      }

      setAiOpen(false);
      setCreateOpen(true);
      const draftMsg = data.draft_created ? ' — brouillon enregistré' : '';
      toast.success(`Facture lue par IA${draftMsg} — vérifiez et confirmez`);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lecture IA');
    } finally { setAiLoading(false); }
  };

  const handleDelete = async (e, invoice) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer la facture ${invoice.internal_ref || invoice.invoice_number} ? Cette action est définitive.`)) return;
    try {
      await axios.delete(`${API_URL}/supplier-invoices/${invoice.id}`);
      toast.success('Facture supprimée');
      fetchInvoices();
      fetchUsage();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  const openPayDialog = (e, invoice) => {
    if (e?.stopPropagation) e.stopPropagation();
    setPayTarget(invoice);
    setPayForm({
      date_paiement: new Date().toISOString().slice(0, 10),
      mode_paiement: invoice.mode_paiement || 'virement',
      reference_paiement: '',
    });
    setPayOpen(true);
  };

  // Refresh the currently-edited invoice from backend (after a status transition)
  const refreshEditingInvoice = async () => {
    if (!editingInvoiceId) return;
    try {
      const res = await axios.get(`${API_URL}/supplier-invoices/${editingInvoiceId}`);
      setEditingInvoice(res.data);
    } catch (e) {}
  };

  // ---------- Validation actions (approve / reject / modification) ----------
  const openValidationDialog = (action) => {
    setValidationAction(action);
    setValidationComment('');
    setValidationOpen(true);
  };

  const handleValidationSubmit = async () => {
    if (!editingInvoiceId || !validationAction) return;
    if ((validationAction === 'reject' || validationAction === 'request-modification') && !validationComment.trim()) {
      toast.error('Un commentaire est obligatoire pour cette action');
      return;
    }
    setValidationLoading(true);
    try {
      const url = `${API_URL}/validation/${editingInvoiceId}/${validationAction}`;
      await axios.post(url, { comment: validationComment || null });
      const labels = { approve: 'Facture approuvée', reject: 'Facture rejetée', 'request-modification': 'Modification demandée' };
      toast.success(labels[validationAction]);
      setValidationOpen(false);
      await refreshEditingInvoice();
      fetchInvoices();
    } catch (e) {
      toast.error(formatAxiosError(e, "Erreur lors de l'action de validation"));
    } finally { setValidationLoading(false); }
  };

  // ---------- Schedule payment ----------
  const openScheduleDialog = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduleForm({
      scheduled_date: tomorrow.toISOString().slice(0, 10),
      mode_paiement: 'prelevement',
      bank_account_id: bankAccounts.find(b => b.is_default)?.id || (bankAccounts[0]?.id || ''),
      notes: '',
    });
    setScheduleOpen(true);
  };

  const handleScheduleSubmit = async () => {
    if (!editingInvoiceId) return;
    if (!scheduleForm.scheduled_date || !scheduleForm.mode_paiement) {
      toast.error('Date et mode de paiement requis');
      return;
    }
    if (scheduleForm.mode_paiement === 'virement_sepa' && !scheduleForm.bank_account_id) {
      toast.error('Compte bancaire requis pour un virement SEPA');
      return;
    }
    setScheduleLoading(true);
    try {
      await axios.post(`${API_URL}/scheduled-payments/`, {
        invoice_id: editingInvoiceId,
        scheduled_date: scheduleForm.scheduled_date,
        mode_paiement: scheduleForm.mode_paiement,
        bank_account_id: scheduleForm.mode_paiement === 'virement_sepa' ? scheduleForm.bank_account_id : null,
        notes: scheduleForm.notes || null,
      });
      toast.success(`Paiement programmé pour le ${formatDateShort(scheduleForm.scheduled_date)}`);
      setScheduleOpen(false);
      fetchInvoices();
    } catch (e) {
      toast.error(formatAxiosError(e, 'Erreur programmation'));
    } finally { setScheduleLoading(false); }
  };

  // ---------- SEPA generation ----------
  const openSepaDialog = () => {
    setSepaForm({
      date_execution: new Date().toISOString().slice(0, 10),
      bank_account_id: bankAccounts.find(b => b.is_default)?.id || (bankAccounts[0]?.id || ''),
    });
    setSepaOpen(true);
  };

  const handleSepaSubmit = async () => {
    if (!sepaForm.bank_account_id) {
      toast.error('Compte bancaire requis');
      return;
    }
    // Batch mode: use the selected IDs from the Validées tab. Single mode:
    // use the currently-edited invoice id.
    const invoice_ids = sepaBatchMode ? Array.from(selectedIds) : (editingInvoiceId ? [editingInvoiceId] : []);
    if (invoice_ids.length === 0) {
      toast.error(sepaBatchMode ? 'Sélectionnez au moins une facture' : 'Facture introuvable');
      return;
    }
    setSepaLoading(true);
    try {
      const res = await axios.post(`${API_URL}/sepa-transfers/`, {
        bank_account_id: sepaForm.bank_account_id,
        date_execution: sepaForm.date_execution,
        invoice_ids,
      });
      const transfer = res.data;
      toast.success(`SEPA généré : ${transfer.reference} (${transfer.nombre_virements || invoice_ids.length} factures)`);

      // Auto-download the XML so the user can transmit it to their bank
      try {
        const xmlRes = await axios.get(`${API_URL}/sepa-transfers/${transfer.id}/xml`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([xmlRes.data], { type: 'application/xml' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${transfer.reference || 'sepa'}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (xmlErr) {
        toast.error("XML généré mais téléchargement automatique impossible. Récupérez-le dans Virements SEPA.");
      }

      setSepaOpen(false);

      if (sepaBatchMode) {
        // Batch flow: clear selection, refresh list, open multi-invoice close prompt
        setSelectedIds(new Set());
        fetchInvoices();
        setBatchPostSepa({
          invoice_ids,
          reference: transfer.reference,
          date_execution: sepaForm.date_execution,
        });
        setBatchPostSepaOpen(true);
        setSepaBatchMode(false);
      } else {
        await refreshEditingInvoice();
        fetchInvoices();
        setPostSepa({
          invoice_id: editingInvoiceId,
          reference: transfer.reference,
          date_execution: sepaForm.date_execution,
        });
        setPostSepaOpen(true);
      }
    } catch (e) {
      toast.error(formatAxiosError(e, 'Erreur génération SEPA'));
    } finally { setSepaLoading(false); }
  };

  // Batch: close N invoices at once after their shared SEPA XML was generated
  const handleBatchPostSepaConfirm = async () => {
    if (!batchPostSepa?.invoice_ids?.length) return;
    setBatchPostSepaLoading(true);
    const { invoice_ids, reference, date_execution } = batchPostSepa;
    const results = await Promise.allSettled(
      invoice_ids.map(invId =>
        axios.put(`${API_URL}/supplier-invoices/${invId}/payment`, {
          payment_status: 'payee',
          date_paiement: date_execution,
          mode_paiement: 'virement_sepa',
          reference_paiement: reference || null,
        })
      )
    );
    const failed = results.filter(r => r.status === 'rejected');
    if (failed.length === 0) {
      toast.success(`${invoice_ids.length} facture(s) marquée(s) comme payées`);
    } else if (failed.length < invoice_ids.length) {
      toast.error(`${invoice_ids.length - failed.length}/${invoice_ids.length} facture(s) clôturées — ${failed.length} échec(s)`);
    } else {
      toast.error('Aucune facture n\'a pu être marquée comme payée');
    }
    setBatchPostSepaOpen(false);
    setBatchPostSepa(null);
    setBatchPostSepaLoading(false);
    fetchInvoices();
  };

  // ---------- Selection helpers (Validées tab batch SEPA) ------------------
  const isBatchable = (inv) => inv.workflow_status === 'validee' && inv.payment_status !== 'payee';

  const toggleSelect = (invId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(invId)) next.delete(invId); else next.add(invId);
      return next;
    });
  };

  const batchableInvoices = useMemo(() => invoices.filter(isBatchable), [invoices]);

  const toggleSelectAll = () => {
    if (selectedIds.size === batchableInvoices.length && batchableInvoices.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(batchableInvoices.map(i => i.id)));
    }
  };

  const selectedTotal = useMemo(
    () => invoices.filter(i => selectedIds.has(i.id)).reduce((s, i) => s + (i.montant_ttc || 0), 0),
    [invoices, selectedIds]
  );

  const openBatchSepaDialog = () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins une facture à virer');
      return;
    }
    if (bankAccounts.length === 0) {
      toast.error("Aucun compte bancaire configuré.");
      return;
    }
    setSepaBatchMode(true);
    setSepaForm({
      date_execution: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
      bank_account_id: bankAccounts.find(b => b.is_default)?.id || bankAccounts[0]?.id || '',
    });
    setSepaOpen(true);
  };

  // Reset selection when the tab changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [tab]);

  // Confirmation: once the XML has been sent to the bank, mark the invoice as
  // paid. Skips marking if user clicks "Plus tard".
  const handlePostSepaConfirm = async () => {
    if (!postSepa?.invoice_id) return;
    setPostSepaLoading(true);
    try {
      await axios.put(`${API_URL}/supplier-invoices/${postSepa.invoice_id}/payment`, {
        payment_status: 'payee',
        date_paiement: postSepa.date_execution,
        mode_paiement: 'virement_sepa',
        reference_paiement: postSepa.reference || null,
      });
      toast.success('Facture marquée comme payée');
      setPostSepaOpen(false);
      setPostSepa(null);
      await refreshEditingInvoice();
      fetchInvoices();
    } catch (e) {
      toast.error(formatAxiosError(e, 'Erreur enregistrement paiement'));
    } finally { setPostSepaLoading(false); }
  };

  // Re-open an existing invoice in the split-view dialog (used for brouillons).
  const openExistingInvoice = async (invoice) => {
    try {
      const res = await axios.get(`${API_URL}/supplier-invoices/${invoice.id}`);
      const inv = res.data;

      // Restore PDF/image preview from the first attachment if present
      const att = (inv.attachments || [])[0];
      if (att?.content_base64) {
        const mime = att.file_type === 'pdf' ? 'application/pdf' :
                     att.file_type === 'png' ? 'image/png' :
                     att.file_type === 'jpg' || att.file_type === 'jpeg' ? 'image/jpeg' :
                     'application/octet-stream';
        setPdfPreviewUrl(`data:${mime};base64,${att.content_base64}`);
        setPdfFileName(att.filename || '');
      } else {
        setPdfPreviewUrl('');
        setPdfFileName('');
      }

      const breakdown = (Array.isArray(inv.tva_breakdown) && inv.tva_breakdown.length)
        ? inv.tva_breakdown.map(r => ({
            taux: String(parseFloat(r.taux) || 20),
            base_ht: parseFloat(r.base_ht) || 0,
            montant_tva: parseFloat(r.montant_tva) || 0,
            compte_comptable: r.compte_comptable || '',
          }))
        : [{ taux: String(inv.taux_tva_principal || 20), base_ht: inv.montant_ht || 0, montant_tva: inv.montant_tva || 0, compte_comptable: '' }];

      setForm({
        ...emptyForm,
        supplier_id: inv.supplier_id || '',
        invoice_number: inv.invoice_number || '',
        date_facture: inv.date_facture || '',
        date_echeance: inv.date_echeance || '',
        description: inv.description || '',
        mode_paiement: inv.mode_paiement || '',
        payment_status: inv.payment_status || 'non_payee',
        date_paiement: inv.date_paiement || '',
        reference_paiement: inv.reference_paiement || '',
        montant_ht: inv.montant_ht || 0,
        montant_ttc: inv.montant_ttc || 0,
        tva_breakdown: breakdown,
        type_document: inv.type_document || 'facture',
      });
      setEditingInvoiceId(inv.id);
      setEditingInvoice(inv);
      // Restore the proposed-supplier panel if the draft has one and is not
      // yet linked to a real supplier.
      if (!inv.supplier_id && inv.proposed_supplier) {
        setInsightSupplier(inv.proposed_supplier);
      } else {
        setInsightSupplier(null);
      }
      setCreateOpen(true);
    } catch (e) {
      toast.error('Erreur ouverture facture');
    }
  };

  const handleConfirmPay = async () => {
    if (!payTarget) return;
    setPayLoading(true);
    try {
      await axios.put(`${API_URL}/supplier-invoices/${payTarget.id}/payment`, {
        payment_status: 'payee',
        date_paiement: payForm.date_paiement,
        mode_paiement: payForm.mode_paiement,
        reference_paiement: payForm.reference_paiement || null,
      });
      toast.success('Facture marquée comme payée');
      setPayOpen(false);
      setPayTarget(null);
      // If this came from inside the create dialog, refresh the editing state
      if (editingInvoice && editingInvoice.id === payTarget.id) {
        setEditingInvoice({ ...editingInvoice, payment_status: 'payee' });
      }
      fetchInvoices();
    } catch (e) {
      toast.error(formatAxiosError(e, 'Erreur enregistrement paiement'));
    } finally { setPayLoading(false); }
  };

  // Revert payment: bring a paid invoice back to non_payee (in case of error)
  const handleUnmarkPaid = async () => {
    if (!editingInvoice) return;
    if (!window.confirm("Annuler le paiement et remettre la facture en non payée ?")) return;
    try {
      await axios.put(`${API_URL}/supplier-invoices/${editingInvoice.id}/payment`, {
        payment_status: 'non_payee',
        date_paiement: null,
        mode_paiement: editingInvoice.mode_paiement || null,
        reference_paiement: null,
      });
      toast.success('Paiement annulé');
      await refreshEditingInvoice();
      fetchInvoices();
    } catch (e) {
      toast.error(formatAxiosError(e, 'Erreur annulation paiement'));
    }
  };

  // ---------- Supplier creation (INSEE-assisted) ----------
  const openSupplierDialog = (preset = null) => {
    setSupSearch(preset?.nom || preset?.siret || '');
    setSupResults([]);
    setSupForm({
      nom: preset?.nom || '',
      siret: preset?.siret || '',
      adresse: preset?.adresse || '',
      code_postal: preset?.code_postal || '',
      ville: preset?.ville || '',
      email: preset?.email || '',
      telephone: preset?.telephone || '',
      iban: preset?.iban || '',
      bic: preset?.bic || '',
      tva_intracommunautaire: preset?.tva_intracommunautaire || '',
    });
    setSupDialogOpen(true);
  };

  const handleSireneSearch = async () => {
    const q = supSearch.trim();
    if (!q) return;
    setSupSearching(true);
    try {
      const res = await axios.get(`${API_URL}/sirene/search`, { params: { q, limit: 10 } });
      setSupResults(res.data?.results || []);
    } catch (e) {
      toast.error("Recherche INSEE indisponible");
    } finally { setSupSearching(false); }
  };

  const pickSireneResult = (r) => {
    setSupForm(prev => ({
      ...prev,
      nom: r.nom_complet || r.nom || prev.nom,
      siret: r.siret || prev.siret,
      adresse: r.adresse || prev.adresse,
      code_postal: r.code_postal || prev.code_postal,
      ville: r.ville || prev.ville,
      tva_intracommunautaire: r.tva_intracommunautaire || prev.tva_intracommunautaire,
    }));
    setSupResults([]);
  };

  const handleCreateSupplierConfirm = async () => {
    if (!supForm.nom) { toast.error('Le nom est obligatoire'); return; }
    setSupSaving(true);
    try {
      const body = {
        nom: supForm.nom,
        siret: supForm.siret || null,
        adresse: supForm.adresse || null,
        code_postal: supForm.code_postal || null,
        ville: supForm.ville || null,
        pays: 'France',
        telephone: supForm.telephone || null,
        email: supForm.email || null,
        iban: supForm.iban || null,
        bic: supForm.bic || null,
        tva_intracommunautaire: supForm.tva_intracommunautaire || null,
      };
      let createdSupplier;
      if (editingInvoiceId && insightSupplier) {
        // We're proposing a supplier for a draft invoice — go through the
        // suppliers.create_from_invoice permission gate and link in one call.
        const res = await axios.post(
          `${API_URL}/supplier-invoices/${editingInvoiceId}/confirm-proposed-supplier`,
          body,
        );
        createdSupplier = res.data?.supplier;
      } else {
        const payload = { ...body, siren: supForm.siret ? supForm.siret.replace(/\s/g, '').slice(0, 9) : null };
        const res = await axios.post(`${API_URL}/suppliers`, payload);
        createdSupplier = res.data;
      }
      toast.success(`Fournisseur "${createdSupplier.nom}" créé`);
      await fetchSuppliers();
      setForm(f => ({ ...f, supplier_id: createdSupplier.id }));
      setInsightSupplier(null);
      setSupDialogOpen(false);
    } catch (e) {
      toast.error(formatAxiosError(e, 'Erreur création fournisseur'));
    } finally { setSupSaving(false); }
  };

  // When supplier is selected manually, fill any TVA row missing a compte with the supplier default.
  // The sentinel "__new__" opens the supplier creation dialog instead.
  const handleSupplierChange = (id) => {
    if (id === '__new__') {
      openSupplierDialog();
      return;
    }
    const s = suppliers.find(x => x.id === id);
    const defaultCompte = s?.compte_comptable_default || '';
    setForm(f => ({
      ...f,
      supplier_id: id,
      tva_breakdown: f.tva_breakdown.map(r => ({
        ...r,
        compte_comptable: r.compte_comptable || defaultCompte,
      })),
    }));
    setInsightSupplier(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Factures fournisseurs</h1>
          <div className="flex items-center gap-3">
            <p className="text-slate-500">{invoices.length} facture(s)</p>
            {usage && !usage.unlimited && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${usage.remaining <= 5 ? 'bg-red-100 text-red-700' : usage.remaining <= 20 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                {usage.count}/{usage.limit} ce mois
              </span>
            )}
          </div>
        </div>
        <Button onClick={() => setAiOpen(true)} className="bg-[#0F5156] hover:bg-[#0d464a]"><Upload className="w-4 h-4 mr-2" /> Déposer une facture</Button>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input placeholder="Rechercher..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} /></div>
        <Select value={supplierFilter} onValueChange={setSupplierFilter}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Fournisseur" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les fournisseurs</SelectItem>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">Toutes</TabsTrigger>
          <TabsTrigger value="brouillon">Brouillons</TabsTrigger>
          <TabsTrigger value="en_validation">En validation</TabsTrigger>
          <TabsTrigger value="validee">Validées</TabsTrigger>
          <TabsTrigger value="rejetee">Rejetées</TabsTrigger>
          <TabsTrigger value="payee">Payées</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'validee' && hasPermission('tresorerie', 'sepa') && selectedIds.size > 0 && (
        <div className="sticky top-2 z-10 flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3 shadow-sm">
          <div className="text-sm">
            <strong>{selectedIds.size}</strong> facture(s) sélectionnée(s) — Total : <strong>{formatCurrency(selectedTotal)}</strong>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Désélectionner</Button>
            <Button onClick={openBatchSepaDialog} className="bg-indigo-600 hover:bg-indigo-700">
              <Send className="w-4 h-4 mr-2" /> Générer SEPA groupé
            </Button>
          </div>
        </div>
      )}

      <Card><CardContent className="p-0">
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <Table><TableHeader><TableRow>
            {tab === 'validee' && (
              <TableHead className="w-10">
                <Checkbox
                  checked={batchableInvoices.length > 0 && selectedIds.size === batchableInvoices.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Tout sélectionner"
                />
              </TableHead>
            )}
            <TableHead>Réf</TableHead><TableHead>Fournisseur</TableHead><TableHead>N° Facture</TableHead><TableHead>Date</TableHead><TableHead>Échéance</TableHead><TableHead>Montant TTC</TableHead><TableHead>Statut</TableHead><TableHead>Paiement</TableHead><TableHead className="w-32 text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {invoices.map(inv => {
              const selectable = tab === 'validee' && isBatchable(inv);
              return (
                <TableRow key={inv.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => {
                  if (inv.workflow_status === 'brouillon') {
                    openExistingInvoice(inv);
                  } else {
                    navigate(`/invoices/${inv.id}`);
                  }
                }}>
                  {tab === 'validee' && (
                    <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                      {selectable ? (
                        <Checkbox
                          checked={selectedIds.has(inv.id)}
                          onCheckedChange={() => toggleSelect(inv.id)}
                          aria-label={`Sélectionner ${inv.internal_ref}`}
                        />
                      ) : <span className="inline-block w-4 h-4" />}
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-sm">{inv.internal_ref}</TableCell>
                  <TableCell className="font-medium">{inv.supplier_name || '-'}</TableCell>
                  <TableCell>{inv.invoice_number}</TableCell>
                  <TableCell>{formatDateShort(inv.date_facture)}</TableCell>
                  <TableCell>{formatDateShort(inv.date_echeance)}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(inv.montant_ttc)}</TableCell>
                  <TableCell><Badge className={getInvoiceStatusColor(inv.workflow_status)}>{formatInvoiceStatus(inv.workflow_status)}{inv.current_step_name ? ` - ${inv.current_step_name}` : ''}</Badge></TableCell>
                  <TableCell><Badge className={getPaymentStatusColor(inv.payment_status)}>{formatPaymentStatus(inv.payment_status)}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={(e) => handleDelete(e, inv)} className="text-red-700 border-red-300 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {invoices.length === 0 && <TableRow><TableCell colSpan={tab === 'validee' ? 10 : 9} className="text-center py-8 text-slate-500">Aucune facture</TableCell></TableRow>}
          </TableBody></Table>
        )}
      </CardContent></Card>

      {/* Split-view: PDF preview + form */}
      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open);
        if (!open) { setForm(emptyForm); setPdfPreviewUrl(''); setPdfFileName(''); setInsightSupplier(null); setEditingInvoiceId(null); setEditingInvoice(null); }
      }}>
        <DialogContent className="max-w-7xl w-[95vw] h-[92vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              Vérifier et enregistrer la facture
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 flex-1 min-h-0 overflow-hidden">
            {/* Left: PDF preview */}
            <div className="border-r bg-slate-50 dark:bg-slate-900 p-3 overflow-hidden flex flex-col">
              {pdfPreviewUrl ? (
                <>
                  <div className="flex items-center gap-2 px-2 pb-2 text-xs text-slate-500 shrink-0">
                    <FileText className="w-3.5 h-3.5" />
                    {pdfFileName}
                  </div>
                  <iframe src={pdfPreviewUrl} title="Aperçu facture" className="flex-1 w-full bg-white rounded border" />
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  Aucun aperçu disponible
                </div>
              )}
            </div>

            {/* Right: form */}
            <div className="p-5 overflow-y-auto space-y-4">
              {/* Document type: facture vs avoir */}
              <div className="flex items-center gap-2">
                <Label className="mb-0">Type de document</Label>
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs ${form.type_document !== 'avoir' ? 'bg-[#0F5156] text-white' : 'bg-white text-slate-600'}`}
                    onClick={() => setForm(f => ({ ...f, type_document: 'facture' }))}
                  >Facture</button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs ${form.type_document === 'avoir' ? 'bg-amber-600 text-white' : 'bg-white text-slate-600'}`}
                    onClick={() => setForm(f => ({ ...f, type_document: 'avoir' }))}
                  >Avoir</button>
                </div>
                {form.type_document === 'avoir' && (
                  <span className="text-xs text-amber-700">Sera enregistré comme avoir fournisseur (crédit)</span>
                )}
              </div>

              {/* Supplier */}
              <div>
                <Label>Fournisseur *</Label>
                <Select value={form.supplier_id} onValueChange={handleSupplierChange}>
                  <SelectTrigger><SelectValue placeholder="Choisir un fournisseur" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__new__" className="text-emerald-700 font-medium">
                      <span className="inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Nouveau fournisseur…</span>
                    </SelectItem>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.nom}{s.siret ? ` — ${s.siret}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!form.supplier_id && insightSupplier && (
                  <div className="mt-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 text-sm">
                    <p className="font-medium text-emerald-900 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> Nouveau fournisseur détecté
                    </p>
                    <p className="font-semibold text-emerald-900 mt-1">{insightSupplier.nom}</p>
                    {insightSupplier.siret && <p className="text-xs text-emerald-700">SIRET {insightSupplier.siret}</p>}
                    {insightSupplier.adresse && <p className="text-xs text-emerald-700">{insightSupplier.adresse}{insightSupplier.code_postal ? `, ${insightSupplier.code_postal} ${insightSupplier.ville || ''}` : ''}</p>}
                    {insightSupplier.iban && <p className="text-xs text-emerald-700">IBAN {insightSupplier.iban}</p>}
                    {hasPermission('suppliers', 'create_from_invoice') ? (
                      <>
                        <p className="text-xs text-emerald-700 mt-2">Ce fournisseur n'est pas dans votre base. Validez sa création ou modifiez avant d'enregistrer.</p>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleConfirmProposedSupplier} disabled={saving || !editingInvoiceId}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Valider et créer
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleCreateSupplierFromInsight} disabled={saving}>
                            Modifier avant création
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-amber-700 mt-2">Vous n'avez pas la permission de créer un fournisseur. Sélectionnez un fournisseur existant dans la liste ci-dessus, ou demandez à un administrateur.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Identifiants facture */}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>N° Facture *</Label><Input value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})} /></div>
                <div><Label>Date facture *</Label><Input type="date" value={form.date_facture} onChange={e => setForm({...form, date_facture: e.target.value})} /></div>
                <div><Label>Date échéance *</Label><Input type="date" value={form.date_echeance} onChange={e => setForm({...form, date_echeance: e.target.value})} /></div>
                <div></div>
              </div>

              {/* Paiement */}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Mode de paiement</Label>
                  <Select value={form.mode_paiement} onValueChange={v => setForm({...form, mode_paiement: v})}>
                    <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>{PAYMENT_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Statut paiement</Label>
                  <Select value={form.payment_status} onValueChange={v => setForm({...form, payment_status: v, date_paiement: v === 'payee' && !form.date_paiement ? new Date().toISOString().slice(0, 10) : form.date_paiement})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.payment_status === 'payee' && (
                  <>
                    <div><Label>Date du paiement</Label><Input type="date" value={form.date_paiement} onChange={e => setForm({...form, date_paiement: e.target.value})} /></div>
                    <div><Label>Référence paiement</Label><Input value={form.reference_paiement} onChange={e => setForm({...form, reference_paiement: e.target.value})} placeholder="N° chèque, virement, …" /></div>
                  </>
                )}
              </div>
              {form.payment_status === 'payee' && (
                <p className="text-xs text-amber-600">⚠ Le paiement ne sera enregistré qu'après validation de la facture par le workflow.</p>
              )}

              {/* Action panel — state-driven by workflow_status + permissions */}
              {editingInvoice && (
                <ActionPanel
                  invoice={editingInvoice}
                  hasPermission={hasPermission}
                  onSubmit={handleSave}
                  onApprove={() => openValidationDialog('approve')}
                  onReject={() => openValidationDialog('reject')}
                  onRequestModification={() => openValidationDialog('request-modification')}
                  onMarkPaid={() => openPayDialog(null, editingInvoice)}
                  onSchedule={openScheduleDialog}
                  onSepa={openSepaDialog}
                  onUnmarkPaid={handleUnmarkPaid}
                />
              )}

              {/* Détail TVA + comptes par ligne */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold">Détail TVA & comptes</Label>
                  <Button variant="outline" size="sm" onClick={addTvaRow}><Plus className="w-3 h-3 mr-1" /> Ligne</Button>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_28px] gap-2 text-xs font-medium text-slate-500 px-1">
                    <div>Compte de charge</div>
                    <div>Taux TVA</div>
                    <div>Base HT</div>
                    <div>TVA</div>
                    <div></div>
                  </div>
                  {form.tva_breakdown.map((r, i) => {
                    const compteNotInPlan = r.compte_comptable && !comptesCharges.find(c => c.numero === r.compte_comptable);
                    return (
                      <div key={i} className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_28px] gap-2 items-start">
                        <div>
                          <Select value={r.compte_comptable} onValueChange={v => updateTvaRow(i, 'compte_comptable', v)}>
                            <SelectTrigger><SelectValue placeholder={comptesCharges.length ? "Compte" : "PCG vide"} /></SelectTrigger>
                            <SelectContent>{comptesCharges.map(c => <SelectItem key={c.numero} value={c.numero}>{c.numero} — {c.libelle}</SelectItem>)}</SelectContent>
                          </Select>
                          {compteNotInPlan && <p className="text-[10px] text-amber-600 mt-1 leading-tight">IA: {r.compte_comptable} non dans PCG</p>}
                        </div>
                        <Select value={String(r.taux)} onValueChange={v => updateTvaRow(i, 'taux', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{FRENCH_TVA_RATES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" step="0.01" value={r.base_ht} onChange={e => updateTvaRow(i, 'base_ht', e.target.value)} />
                        <Input type="number" step="0.01" value={r.montant_tva} onChange={e => updateTvaRow(i, 'montant_tva', e.target.value)} />
                        <div className="flex items-center justify-center pt-1">
                          {form.tva_breakdown.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeTvaRow(i)} className="p-1 h-7"><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Totals */}
              <div className="rounded-lg border p-3 space-y-1 bg-slate-50 dark:bg-slate-800 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Total HT</span><strong>{formatCurrency(totalHt)}</strong></div>
                <div className="flex justify-between"><span className="text-slate-500">Total TVA</span><strong>{formatCurrency(totalTva)}</strong></div>
                <div className="flex justify-between text-lg pt-1 border-t"><span>Total TTC</span><strong className="text-[#0F5156]">{formatCurrency(totalTtc)}</strong></div>
              </div>

              {/* Description (optional) */}
              <div><Label>Description / Notes</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t bg-white dark:bg-slate-900 shrink-0">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingInvoiceId ? 'Valider la facture' : 'Enregistrer la facture'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier creation Dialog (INSEE-assisted) */}
      <Dialog open={supDialogOpen} onOpenChange={setSupDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle><Plus className="w-5 h-5 inline mr-2 text-emerald-600" />Nouveau fournisseur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* INSEE search */}
            <div>
              <Label>Recherche INSEE (SIRET ou nom)</Label>
              <div className="flex gap-2">
                <Input
                  value={supSearch}
                  onChange={e => setSupSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSireneSearch(); } }}
                  placeholder="123456789 ou 12345678901234 ou nom..."
                />
                <Button variant="outline" onClick={handleSireneSearch} disabled={supSearching || !supSearch.trim()}>
                  {supSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">L'API gouv.fr remplit automatiquement les champs ci-dessous.</p>
            </div>

            {/* Search results */}
            {supResults.length > 0 && (
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {supResults.map((r, i) => (
                  <button
                    key={r.siret || r.siren || i}
                    type="button"
                    onClick={() => pickSireneResult(r)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                  >
                    <p className="font-medium text-slate-900">{r.nom_complet || r.nom}</p>
                    <p className="text-xs text-slate-500">
                      {r.siret ? `SIRET ${r.siret}` : r.siren ? `SIREN ${r.siren}` : ''}
                      {r.code_postal ? ` · ${r.code_postal} ${r.ville || ''}` : ''}
                    </p>
                  </button>
                ))}
              </div>
            )}

            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-600 mb-2">Données fournisseur</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nom *</Label><Input value={supForm.nom} onChange={e => setSupForm({...supForm, nom: e.target.value})} /></div>
                <div><Label>SIRET</Label><Input value={supForm.siret} onChange={e => setSupForm({...supForm, siret: e.target.value})} placeholder="14 chiffres" /></div>
                <div><Label>TVA intracom.</Label><Input value={supForm.tva_intracommunautaire} onChange={e => setSupForm({...supForm, tva_intracommunautaire: e.target.value})} /></div>
                <div className="col-span-2"><Label>Adresse</Label><Input value={supForm.adresse} onChange={e => setSupForm({...supForm, adresse: e.target.value})} /></div>
                <div><Label>Code postal</Label><Input value={supForm.code_postal} onChange={e => setSupForm({...supForm, code_postal: e.target.value})} /></div>
                <div><Label>Ville</Label><Input value={supForm.ville} onChange={e => setSupForm({...supForm, ville: e.target.value})} /></div>
                <div><Label>Téléphone</Label><Input value={supForm.telephone} onChange={e => setSupForm({...supForm, telephone: e.target.value})} /></div>
                <div><Label>Email</Label><Input type="email" value={supForm.email} onChange={e => setSupForm({...supForm, email: e.target.value})} /></div>
                <div><Label>IBAN</Label><Input value={supForm.iban} onChange={e => setSupForm({...supForm, iban: e.target.value})} /></div>
                <div><Label>BIC</Label><Input value={supForm.bic} onChange={e => setSupForm({...supForm, bic: e.target.value})} /></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateSupplierConfirm} disabled={supSaving || !supForm.nom} className="bg-emerald-600 hover:bg-emerald-700">
              {supSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer le fournisseur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as paid Dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle><CheckCircle2 className="w-5 h-5 inline mr-2 text-green-600" />Marquer comme payée</DialogTitle></DialogHeader>
          {payTarget && (
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <p className="text-slate-500">Facture <span className="font-mono">{payTarget.internal_ref}</span> · {payTarget.supplier_name}</p>
                <p className="text-lg font-bold text-slate-900 mt-1">{formatCurrency(payTarget.montant_ttc)}</p>
              </div>
              <div><Label>Date du paiement *</Label><Input type="date" value={payForm.date_paiement} onChange={e => setPayForm({...payForm, date_paiement: e.target.value})} /></div>
              <div><Label>Mode de paiement *</Label>
                <Select value={payForm.mode_paiement} onValueChange={v => setPayForm({...payForm, mode_paiement: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Référence (n° chèque, virement…)</Label><Input value={payForm.reference_paiement} onChange={e => setPayForm({...payForm, reference_paiement: e.target.value})} placeholder="Optionnel" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Annuler</Button>
            <Button onClick={handleConfirmPay} disabled={payLoading || !payForm.date_paiement || !payForm.mode_paiement} className="bg-green-600 hover:bg-green-700">
              {payLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation action Dialog (approve / reject / modify) */}
      <Dialog open={validationOpen} onOpenChange={setValidationOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>
            {validationAction === 'approve' && <><ThumbsUp className="w-5 h-5 inline mr-2 text-green-600" />Approuver la facture</>}
            {validationAction === 'reject' && <><ThumbsDown className="w-5 h-5 inline mr-2 text-red-600" />Rejeter la facture</>}
            {validationAction === 'request-modification' && <><Edit3 className="w-5 h-5 inline mr-2 text-orange-600" />Demander une modification</>}
          </DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Commentaire {(validationAction === 'reject' || validationAction === 'request-modification') && '*'}</Label>
              <Textarea
                value={validationComment}
                onChange={e => setValidationComment(e.target.value)}
                placeholder={validationAction === 'approve' ? 'Optionnel' : 'Motif obligatoire (visible par le saisisseur)'}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidationOpen(false)}>Annuler</Button>
            <Button onClick={handleValidationSubmit} disabled={validationLoading}>
              {validationLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule payment Dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle><CalendarClock className="w-5 h-5 inline mr-2 text-blue-600" />Programmer le paiement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date prévue *</Label><Input type="date" value={scheduleForm.scheduled_date} onChange={e => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})} /></div>
            <div><Label>Mode de paiement *</Label>
              <Select value={scheduleForm.mode_paiement} onValueChange={v => setScheduleForm({...scheduleForm, mode_paiement: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prelevement">Prélèvement SEPA</SelectItem>
                  <SelectItem value="virement">Virement</SelectItem>
                  <SelectItem value="virement_sepa">Virement SEPA (génération XML)</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="carte">Carte bancaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scheduleForm.mode_paiement === 'virement_sepa' && (
              <div><Label>Compte bancaire émetteur *</Label>
                <Select value={scheduleForm.bank_account_id} onValueChange={v => setScheduleForm({...scheduleForm, bank_account_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Choisir un compte" /></SelectTrigger>
                  <SelectContent>{bankAccounts.map(b => <SelectItem key={b.id} value={b.id}>{b.label || b.titulaire} — {b.iban}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Notes</Label><Input value={scheduleForm.notes} onChange={e => setScheduleForm({...scheduleForm, notes: e.target.value})} placeholder="Optionnel" /></div>
            <p className="text-xs text-slate-500">Le paiement sera exécuté manuellement depuis la page « Paiements programmés » à la date prévue.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>Annuler</Button>
            <Button onClick={handleScheduleSubmit} disabled={scheduleLoading} className="bg-blue-600 hover:bg-blue-700">
              {scheduleLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Programmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEPA generation Dialog */}
      <Dialog open={sepaOpen} onOpenChange={(open) => { setSepaOpen(open); if (!open) setSepaBatchMode(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle><Send className="w-5 h-5 inline mr-2 text-indigo-600" />{sepaBatchMode ? `Virement SEPA groupé (${selectedIds.size} factures)` : 'Générer fichier SEPA (XML)'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {sepaBatchMode && (
              <div className="rounded bg-indigo-50 border border-indigo-200 p-2 text-xs text-indigo-800">
                Total à virer : <strong>{formatCurrency(selectedTotal)}</strong>
              </div>
            )}
            <div><Label>Date d'exécution *</Label><Input type="date" value={sepaForm.date_execution} onChange={e => setSepaForm({...sepaForm, date_execution: e.target.value})} /></div>
            <div><Label>Compte bancaire émetteur *</Label>
              <Select value={sepaForm.bank_account_id} onValueChange={v => setSepaForm({...sepaForm, bank_account_id: v})}>
                <SelectTrigger><SelectValue placeholder="Choisir un compte" /></SelectTrigger>
                <SelectContent>{bankAccounts.map(b => <SelectItem key={b.id} value={b.id}>{b.label || b.titulaire} — {b.iban}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">Un fichier pain.001.001.03 sera généré et téléchargé automatiquement. Les factures seront marquées payées seulement après votre confirmation à l'étape suivante.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSepaOpen(false)}>Annuler</Button>
            <Button onClick={handleSepaSubmit} disabled={sepaLoading || !sepaForm.bank_account_id} className="bg-indigo-600 hover:bg-indigo-700">
              {sepaLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Générer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-SEPA: ask the user whether to close the invoice now */}
      <Dialog open={postSepaOpen} onOpenChange={(open) => { setPostSepaOpen(open); if (!open) setPostSepa(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" />Clôturer la facture ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>Le fichier SEPA <strong>{postSepa?.reference}</strong> a été généré et téléchargé. Transmettez-le à votre banque puis indiquez à l'application si la facture peut être marquée comme payée.</p>
            <p className="text-xs text-slate-500">Date du paiement (= date d'exécution du virement) : <strong>{postSepa?.date_execution}</strong>. Vous pourrez toujours modifier l'état de paiement plus tard si nécessaire.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPostSepaOpen(false); setPostSepa(null); }} disabled={postSepaLoading}>Plus tard</Button>
            <Button onClick={handlePostSepaConfirm} disabled={postSepaLoading} className="bg-emerald-600 hover:bg-emerald-700">
              {postSepaLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Marquer comme payée
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch post-SEPA confirmation */}
      <Dialog open={batchPostSepaOpen} onOpenChange={(open) => { setBatchPostSepaOpen(open); if (!open) setBatchPostSepa(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" />Clôturer les factures ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>Le fichier SEPA <strong>{batchPostSepa?.reference}</strong> regroupant <strong>{batchPostSepa?.invoice_ids?.length}</strong> facture(s) a été généré et téléchargé.</p>
            <p>Transmettez-le à votre banque, puis indiquez si vous souhaitez clôturer (marquer comme payées) toutes ces factures maintenant.</p>
            <p className="text-xs text-slate-500">Date du paiement appliquée à chacune : <strong>{batchPostSepa?.date_execution}</strong>.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBatchPostSepaOpen(false); setBatchPostSepa(null); }} disabled={batchPostSepaLoading}>Plus tard</Button>
            <Button onClick={handleBatchPostSepaConfirm} disabled={batchPostSepaLoading} className="bg-emerald-600 hover:bg-emerald-700">
              {batchPostSepaLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Marquer comme payées
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Import Dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle><Upload className="w-5 h-5 inline mr-2" />Déposer une facture fournisseur</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Déposez un PDF ou une image de facture. L'IA extraira automatiquement les informations pour vérification.</p>
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            {aiLoading ? <Loader2 className="w-8 h-8 animate-spin mx-auto" /> : (
              <label className="cursor-pointer"><Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" /><p className="text-sm text-slate-600">Cliquez pour choisir un fichier</p><p className="text-xs text-slate-400">PDF, JPG, PNG</p>
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleAiImport} />
              </label>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
